'use client';

import { createClient } from '@/lib/supabase/client';

// ─── Types ────────────────────────────────────────────────────────────────────

export type ShiftStatus = 'assigned' | 'open' | 'cancelled';
export type StaffRole = 'rn' | 'cna' | 'lpn' | 'np' | 'md';

export interface Shift {
  id: string;
  staffName: string | null;
  role: StaffRole;
  specialty: string;
  facility: string;
  shiftDate: string;
  startTime: string;
  endTime: string;
  status: ShiftStatus;
  hasConflict: boolean;
}

export type JobStatus = 'active' | 'paused' | 'closed';
export type JobType = 'Full-time' | 'Part-time' | 'Contract' | 'Per Diem';

export interface JobPosting {
  id: string;
  title: string;
  department: string;
  location: string;
  jobType: JobType;
  status: JobStatus;
  postedDate: string;
  closingDate: string;
  description: string;
  requirements: string[];
  payRange: string;
}

export type PipelineStage = 'applied' | 'screening' | 'interview' | 'offer' | 'hired';

export interface Applicant {
  id: string;
  name: string;
  role: string;
  jobId: string | null;
  jobTitle: string;
  email: string;
  phone: string;
  stage: PipelineStage;
  appliedDate: string;
  lastUpdated: string;
  resumeFileName: string | null;
  resumeUrl: string | null;
  notes: string;
  rating: number;
  experience: string;
  location: string;
  tags: string[];
}

export type CredentialStatus = 'active' | 'expiring_soon' | 'expired' | 'pending_renewal';
export type CredentialCategory = 'license' | 'certification' | 'training' | 'background';

export interface Credential {
  id: string;
  staffId: string;
  staffName: string;
  role: string;
  department: string;
  credentialType: string;
  credentialName: string;
  issuingBody: string;
  licenseNumber: string;
  issueDate: string;
  expiryDate: string;
  status: CredentialStatus;
  daysUntilExpiry: number;
  renewalSubmitted: boolean;
  notes: string;
  category: CredentialCategory;
}

export type ApprovalStatus = 'approved' | 'pending' | 'rejected';

export interface Timesheet {
  id: string;
  staffName: string;
  role: StaffRole;
  specialty: string;
  facility: string;
  shiftDate: string;
  clockIn: string;
  clockOut: string;
  breakMin: number;
  regularHours: number;
  otHours: number;
  approvalStatus: ApprovalStatus;
  missedPunch: boolean;
}

// ─── Audit Types ──────────────────────────────────────────────────────────────

export type AuditActionType =
  | 'role_change' |'credential_update' |'shift_modification' |'approval' |'login' |'logout' |'user_created' |'user_deactivated' |'invoice_created' |'invoice_updated' |'timesheet_approved' |'timesheet_rejected' |'job_posted' |'applicant_stage_changed' |'settings_updated';

export type AuditSeverity = 'info' | 'warning' | 'critical';

export interface AuditLog {
  id: string;
  performedBy: string | null;
  performedByName: string;
  performedByRole: string;
  actionType: AuditActionType;
  severity: AuditSeverity;
  targetEntity: string;
  targetId: string | null;
  description: string;
  beforeState: Record<string, unknown> | null;
  afterState: Record<string, unknown> | null;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
}

export interface AuditFilters {
  search?: string;
  actionType?: AuditActionType;
  severity?: AuditSeverity;
  dateFrom?: string;
  dateTo?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isSchemaError(error: { code?: string; message?: string }): boolean {
  if (!error) return false;
  if (error.code && typeof error.code === 'string') {
    const cls = error.code.substring(0, 2);
    if (cls === '42' || cls === '08') return true;
    if (cls === '23') return false;
  }
  if (error.message) {
    return /relation.*does not exist|column.*does not exist|function.*does not exist|syntax error|type.*does not exist/i.test(error.message);
  }
  return false;
}

// ─── Shifts Service ───────────────────────────────────────────────────────────

export const shiftsService = {
  async getAll(): Promise<Shift[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('shifts')
        .select('*')
        .order('shift_date', { ascending: true })
        .order('start_time', { ascending: true });
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return (data || []).map((r) => ({
        id: r.id,
        staffName: r.staff_name,
        role: r.role as StaffRole,
        specialty: r.specialty,
        facility: r.facility,
        shiftDate: r.shift_date,
        startTime: r.start_time,
        endTime: r.end_time,
        status: r.status as ShiftStatus,
        hasConflict: r.has_conflict,
      }));
    } catch (e: unknown) {
      const err = e as { message?: string };
      console.error('shiftsService.getAll error:', err?.message);
      throw e;
    }
  },

  async update(id: string, updates: Partial<{ staffName: string | null; status: ShiftStatus; hasConflict: boolean }>): Promise<Shift | null> {
    const supabase = createClient();
    const dbUpdates: Record<string, unknown> = {};
    if ('staffName' in updates) dbUpdates.staff_name = updates.staffName;
    if ('status' in updates) dbUpdates.status = updates.status;
    if ('hasConflict' in updates) dbUpdates.has_conflict = updates.hasConflict;
    try {
      const { data, error } = await supabase
        .from('shifts')
        .update(dbUpdates)
        .eq('id', id)
        .select()
        .single();
      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }
      return {
        id: data.id,
        staffName: data.staff_name,
        role: data.role as StaffRole,
        specialty: data.specialty,
        facility: data.facility,
        shiftDate: data.shift_date,
        startTime: data.start_time,
        endTime: data.end_time,
        status: data.status as ShiftStatus,
        hasConflict: data.has_conflict,
      };
    } catch (e: unknown) {
      const err = e as { message?: string };
      console.error('shiftsService.update error:', err?.message);
      throw e;
    }
  },

  subscribe(onInsert: (s: Shift) => void, onUpdate: (s: Shift) => void, onDelete: (id: string) => void) {
    const supabase = createClient();
    const channel = supabase
      .channel('shifts_realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'shifts' }, (payload) => {
        const map = (r: Record<string, unknown>): Shift => ({
          id: r.id as string,
          staffName: r.staff_name as string | null,
          role: r.role as StaffRole,
          specialty: r.specialty as string,
          facility: r.facility as string,
          shiftDate: r.shift_date as string,
          startTime: r.start_time as string,
          endTime: r.end_time as string,
          status: r.status as ShiftStatus,
          hasConflict: r.has_conflict as boolean,
        });
        if (payload.eventType === 'INSERT') onInsert(map(payload.new as Record<string, unknown>));
        else if (payload.eventType === 'UPDATE') onUpdate(map(payload.new as Record<string, unknown>));
        else if (payload.eventType === 'DELETE') onDelete((payload.old as Record<string, unknown>).id as string);
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  },
};

// ─── Job Postings Service ─────────────────────────────────────────────────────

export const jobPostingsService = {
  async getAll(): Promise<JobPosting[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('job_postings')
        .select('*')
        .order('posted_date', { ascending: false });
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return (data || []).map((r) => ({
        id: r.id,
        title: r.title,
        department: r.department,
        location: r.location,
        jobType: r.job_type as JobType,
        status: r.status as JobStatus,
        postedDate: r.posted_date,
        closingDate: r.closing_date,
        description: r.description,
        requirements: r.requirements || [],
        payRange: r.pay_range,
      }));
    } catch (e: unknown) {
      const err = e as { message?: string };
      console.error('jobPostingsService.getAll error:', err?.message);
      throw e;
    }
  },

  async create(job: Omit<JobPosting, 'id'>): Promise<JobPosting | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('job_postings')
        .insert({
          title: job.title,
          department: job.department,
          location: job.location,
          job_type: job.jobType,
          status: job.status,
          posted_date: job.postedDate,
          closing_date: job.closingDate,
          description: job.description,
          requirements: job.requirements,
          pay_range: job.payRange,
        })
        .select()
        .single();
      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }
      return {
        id: data.id,
        title: data.title,
        department: data.department,
        location: data.location,
        jobType: data.job_type as JobType,
        status: data.status as JobStatus,
        postedDate: data.posted_date,
        closingDate: data.closing_date,
        description: data.description,
        requirements: data.requirements || [],
        payRange: data.pay_range,
      };
    } catch (e: unknown) {
      const err = e as { message?: string };
      console.error('jobPostingsService.create error:', err?.message);
      throw e;
    }
  },

  async update(id: string, updates: Partial<JobPosting>): Promise<JobPosting | null> {
    const supabase = createClient();
    const dbUpdates: Record<string, unknown> = {};
    if (updates.title !== undefined) dbUpdates.title = updates.title;
    if (updates.status !== undefined) dbUpdates.status = updates.status;
    if (updates.department !== undefined) dbUpdates.department = updates.department;
    if (updates.location !== undefined) dbUpdates.location = updates.location;
    if (updates.jobType !== undefined) dbUpdates.job_type = updates.jobType;
    if (updates.closingDate !== undefined) dbUpdates.closing_date = updates.closingDate;
    if (updates.description !== undefined) dbUpdates.description = updates.description;
    if (updates.requirements !== undefined) dbUpdates.requirements = updates.requirements;
    if (updates.payRange !== undefined) dbUpdates.pay_range = updates.payRange;
    try {
      const { data, error } = await supabase
        .from('job_postings')
        .update(dbUpdates)
        .eq('id', id)
        .select()
        .single();
      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }
      return {
        id: data.id,
        title: data.title,
        department: data.department,
        location: data.location,
        jobType: data.job_type as JobType,
        status: data.status as JobStatus,
        postedDate: data.posted_date,
        closingDate: data.closing_date,
        description: data.description,
        requirements: data.requirements || [],
        payRange: data.pay_range,
      };
    } catch (e: unknown) {
      const err = e as { message?: string };
      console.error('jobPostingsService.update error:', err?.message);
      throw e;
    }
  },

  subscribe(onInsert: (j: JobPosting) => void, onUpdate: (j: JobPosting) => void, onDelete: (id: string) => void) {
    const supabase = createClient();
    const map = (r: Record<string, unknown>): JobPosting => ({
      id: r.id as string,
      title: r.title as string,
      department: r.department as string,
      location: r.location as string,
      jobType: r.job_type as JobType,
      status: r.status as JobStatus,
      postedDate: r.posted_date as string,
      closingDate: r.closing_date as string,
      description: r.description as string,
      requirements: (r.requirements as string[]) || [],
      payRange: r.pay_range as string,
    });
    const channel = supabase
      .channel('job_postings_realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'job_postings' }, (payload) => {
        if (payload.eventType === 'INSERT') onInsert(map(payload.new as Record<string, unknown>));
        else if (payload.eventType === 'UPDATE') onUpdate(map(payload.new as Record<string, unknown>));
        else if (payload.eventType === 'DELETE') onDelete((payload.old as Record<string, unknown>).id as string);
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  },
};

// ─── Applicants Service ───────────────────────────────────────────────────────

export const applicantsService = {
  async getAll(): Promise<Applicant[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('applicants')
        .select('*')
        .order('applied_date', { ascending: false });
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return (data || []).map((r) => ({
        id: r.id,
        name: r.name,
        role: r.role,
        jobId: r.job_id,
        jobTitle: r.job_title,
        email: r.email,
        phone: r.phone,
        stage: r.stage as PipelineStage,
        appliedDate: r.applied_date,
        lastUpdated: r.last_updated,
        resumeFileName: r.resume_file_name,
        resumeUrl: r.resume_url,
        notes: r.notes,
        rating: r.rating,
        experience: r.experience,
        location: r.location,
        tags: r.tags || [],
      }));
    } catch (e: unknown) {
      const err = e as { message?: string };
      console.error('applicantsService.getAll error:', err?.message);
      throw e;
    }
  },

  async create(applicant: Omit<Applicant, 'id'>): Promise<Applicant | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('applicants')
        .insert({
          name: applicant.name,
          role: applicant.role,
          job_id: applicant.jobId,
          job_title: applicant.jobTitle,
          email: applicant.email,
          phone: applicant.phone,
          stage: applicant.stage,
          applied_date: applicant.appliedDate,
          last_updated: applicant.lastUpdated,
          resume_file_name: applicant.resumeFileName,
          resume_url: applicant.resumeUrl,
          notes: applicant.notes,
          rating: applicant.rating,
          experience: applicant.experience,
          location: applicant.location,
          tags: applicant.tags,
        })
        .select()
        .single();
      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }
      return {
        id: data.id,
        name: data.name,
        role: data.role,
        jobId: data.job_id,
        jobTitle: data.job_title,
        email: data.email,
        phone: data.phone,
        stage: data.stage as PipelineStage,
        appliedDate: data.applied_date,
        lastUpdated: data.last_updated,
        resumeFileName: data.resume_file_name,
        resumeUrl: data.resume_url,
        notes: data.notes,
        rating: data.rating,
        experience: data.experience,
        location: data.location,
        tags: data.tags || [],
      };
    } catch (e: unknown) {
      const err = e as { message?: string };
      console.error('applicantsService.create error:', err?.message);
      throw e;
    }
  },

  async update(id: string, updates: Partial<Applicant>): Promise<Applicant | null> {
    const supabase = createClient();
    const dbUpdates: Record<string, unknown> = {};
    if (updates.stage !== undefined) dbUpdates.stage = updates.stage;
    if (updates.rating !== undefined) dbUpdates.rating = updates.rating;
    if (updates.notes !== undefined) dbUpdates.notes = updates.notes;
    if (updates.resumeFileName !== undefined) dbUpdates.resume_file_name = updates.resumeFileName;
    if (updates.resumeUrl !== undefined) dbUpdates.resume_url = updates.resumeUrl;
    if (updates.tags !== undefined) dbUpdates.tags = updates.tags;
    if (updates.lastUpdated !== undefined) dbUpdates.last_updated = updates.lastUpdated;
    try {
      const { data, error } = await supabase
        .from('applicants')
        .update(dbUpdates)
        .eq('id', id)
        .select()
        .single();
      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }
      return {
        id: data.id,
        name: data.name,
        role: data.role,
        jobId: data.job_id,
        jobTitle: data.job_title,
        email: data.email,
        phone: data.phone,
        stage: data.stage as PipelineStage,
        appliedDate: data.applied_date,
        lastUpdated: data.last_updated,
        resumeFileName: data.resume_file_name,
        resumeUrl: data.resume_url,
        notes: data.notes,
        rating: data.rating,
        experience: data.experience,
        location: data.location,
        tags: data.tags || [],
      };
    } catch (e: unknown) {
      const err = e as { message?: string };
      console.error('applicantsService.update error:', err?.message);
      throw e;
    }
  },

  subscribe(onInsert: (a: Applicant) => void, onUpdate: (a: Applicant) => void, onDelete: (id: string) => void) {
    const supabase = createClient();
    const map = (r: Record<string, unknown>): Applicant => ({
      id: r.id as string,
      name: r.name as string,
      role: r.role as string,
      jobId: r.job_id as string | null,
      jobTitle: r.job_title as string,
      email: r.email as string,
      phone: r.phone as string,
      stage: r.stage as PipelineStage,
      appliedDate: r.applied_date as string,
      lastUpdated: r.last_updated as string,
      resumeFileName: r.resume_file_name as string | null,
      resumeUrl: r.resume_url as string | null,
      notes: r.notes as string,
      rating: r.rating as number,
      experience: r.experience as string,
      location: r.location as string,
      tags: (r.tags as string[]) || [],
    });
    const channel = supabase
      .channel('applicants_realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'applicants' }, (payload) => {
        if (payload.eventType === 'INSERT') onInsert(map(payload.new as Record<string, unknown>));
        else if (payload.eventType === 'UPDATE') onUpdate(map(payload.new as Record<string, unknown>));
        else if (payload.eventType === 'DELETE') onDelete((payload.old as Record<string, unknown>).id as string);
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  },
};

// ─── Credentials Service ──────────────────────────────────────────────────────

export const credentialsService = {
  async getAll(): Promise<Credential[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('credentials')
        .select('*')
        .order('days_until_expiry', { ascending: true });
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return (data || []).map((r) => ({
        id: r.id,
        staffId: r.staff_id,
        staffName: r.staff_name,
        role: r.role,
        department: r.department,
        credentialType: r.credential_type,
        credentialName: r.credential_name,
        issuingBody: r.issuing_body,
        licenseNumber: r.license_number,
        issueDate: r.issue_date,
        expiryDate: r.expiry_date,
        status: r.status as CredentialStatus,
        daysUntilExpiry: r.days_until_expiry,
        renewalSubmitted: r.renewal_submitted,
        notes: r.notes,
        category: r.category as CredentialCategory,
      }));
    } catch (e: unknown) {
      const err = e as { message?: string };
      console.error('credentialsService.getAll error:', err?.message);
      throw e;
    }
  },

  async update(id: string, updates: Partial<Credential>): Promise<Credential | null> {
    const supabase = createClient();
    const dbUpdates: Record<string, unknown> = {};
    if (updates.renewalSubmitted !== undefined) dbUpdates.renewal_submitted = updates.renewalSubmitted;
    if (updates.status !== undefined) dbUpdates.status = updates.status;
    if (updates.notes !== undefined) dbUpdates.notes = updates.notes;
    try {
      const { data, error } = await supabase
        .from('credentials')
        .update(dbUpdates)
        .eq('id', id)
        .select()
        .single();
      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }
      return {
        id: data.id,
        staffId: data.staff_id,
        staffName: data.staff_name,
        role: data.role,
        department: data.department,
        credentialType: data.credential_type,
        credentialName: data.credential_name,
        issuingBody: data.issuing_body,
        licenseNumber: data.license_number,
        issueDate: data.issue_date,
        expiryDate: data.expiry_date,
        status: data.status as CredentialStatus,
        daysUntilExpiry: data.days_until_expiry,
        renewalSubmitted: data.renewal_submitted,
        notes: data.notes,
        category: data.category as CredentialCategory,
      };
    } catch (e: unknown) {
      const err = e as { message?: string };
      console.error('credentialsService.update error:', err?.message);
      throw e;
    }
  },

  subscribe(onInsert: (c: Credential) => void, onUpdate: (c: Credential) => void, onDelete: (id: string) => void) {
    const supabase = createClient();
    const map = (r: Record<string, unknown>): Credential => ({
      id: r.id as string,
      staffId: r.staff_id as string,
      staffName: r.staff_name as string,
      role: r.role as string,
      department: r.department as string,
      credentialType: r.credential_type as string,
      credentialName: r.credential_name as string,
      issuingBody: r.issuing_body as string,
      licenseNumber: r.license_number as string,
      issueDate: r.issue_date as string,
      expiryDate: r.expiry_date as string,
      status: r.status as CredentialStatus,
      daysUntilExpiry: r.days_until_expiry as number,
      renewalSubmitted: r.renewal_submitted as boolean,
      notes: r.notes as string,
      category: r.category as CredentialCategory,
    });
    const channel = supabase
      .channel('credentials_realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'credentials' }, (payload) => {
        if (payload.eventType === 'INSERT') onInsert(map(payload.new as Record<string, unknown>));
        else if (payload.eventType === 'UPDATE') onUpdate(map(payload.new as Record<string, unknown>));
        else if (payload.eventType === 'DELETE') onDelete((payload.old as Record<string, unknown>).id as string);
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  },
};

// ─── Timesheets Service ───────────────────────────────────────────────────────

export const timesheetsService = {
  async getAll(): Promise<Timesheet[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('timesheets')
        .select('*')
        .order('shift_date', { ascending: false });
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return (data || []).map((r) => ({
        id: r.id,
        staffName: r.staff_name,
        role: r.role as StaffRole,
        specialty: r.specialty,
        facility: r.facility,
        shiftDate: r.shift_date,
        clockIn: r.clock_in,
        clockOut: r.clock_out,
        breakMin: r.break_min,
        regularHours: parseFloat(r.regular_hours),
        otHours: parseFloat(r.ot_hours),
        approvalStatus: r.approval_status as ApprovalStatus,
        missedPunch: r.missed_punch,
      }));
    } catch (e: unknown) {
      const err = e as { message?: string };
      console.error('timesheetsService.getAll error:', err?.message);
      throw e;
    }
  },

  async update(id: string, updates: Partial<Timesheet>): Promise<Timesheet | null> {
    const supabase = createClient();
    const dbUpdates: Record<string, unknown> = {};
    if (updates.approvalStatus !== undefined) dbUpdates.approval_status = updates.approvalStatus;
    if (updates.clockIn !== undefined) dbUpdates.clock_in = updates.clockIn;
    if (updates.clockOut !== undefined) dbUpdates.clock_out = updates.clockOut;
    if (updates.breakMin !== undefined) dbUpdates.break_min = updates.breakMin;
    if (updates.regularHours !== undefined) dbUpdates.regular_hours = updates.regularHours;
    if (updates.otHours !== undefined) dbUpdates.ot_hours = updates.otHours;
    if (updates.missedPunch !== undefined) dbUpdates.missed_punch = updates.missedPunch;
    try {
      const { data, error } = await supabase
        .from('timesheets')
        .update(dbUpdates)
        .eq('id', id)
        .select()
        .single();
      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }
      return {
        id: data.id,
        staffName: data.staff_name,
        role: data.role as StaffRole,
        specialty: data.specialty,
        facility: data.facility,
        shiftDate: data.shift_date,
        clockIn: data.clock_in,
        clockOut: data.clock_out,
        breakMin: data.break_min,
        regularHours: parseFloat(data.regular_hours),
        otHours: parseFloat(data.ot_hours),
        approvalStatus: data.approval_status as ApprovalStatus,
        missedPunch: data.missed_punch,
      };
    } catch (e: unknown) {
      const err = e as { message?: string };
      console.error('timesheetsService.update error:', err?.message);
      throw e;
    }
  },

  async bulkUpdateStatus(ids: string[], status: ApprovalStatus): Promise<void> {
    const supabase = createClient();
    try {
      const { error } = await supabase
        .from('timesheets')
        .update({ approval_status: status })
        .in('id', ids);
      if (error) {
        if (isSchemaError(error)) throw error;
      }
    } catch (e: unknown) {
      const err = e as { message?: string };
      console.error('timesheetsService.bulkUpdateStatus error:', err?.message);
      throw e;
    }
  },

  subscribe(onInsert: (t: Timesheet) => void, onUpdate: (t: Timesheet) => void, onDelete: (id: string) => void) {
    const supabase = createClient();
    const map = (r: Record<string, unknown>): Timesheet => ({
      id: r.id as string,
      staffName: r.staff_name as string,
      role: r.role as StaffRole,
      specialty: r.specialty as string,
      facility: r.facility as string,
      shiftDate: r.shift_date as string,
      clockIn: r.clock_in as string,
      clockOut: r.clock_out as string,
      breakMin: r.break_min as number,
      regularHours: parseFloat(r.regular_hours as string),
      otHours: parseFloat(r.ot_hours as string),
      approvalStatus: r.approval_status as ApprovalStatus,
      missedPunch: r.missed_punch as boolean,
    });
    const channel = supabase
      .channel('timesheets_realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'timesheets' }, (payload) => {
        if (payload.eventType === 'INSERT') onInsert(map(payload.new as Record<string, unknown>));
        else if (payload.eventType === 'UPDATE') onUpdate(map(payload.new as Record<string, unknown>));
        else if (payload.eventType === 'DELETE') onDelete((payload.old as Record<string, unknown>).id as string);
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  },
};

// ─── Invoicing Service ────────────────────────────────────────────────────────

export type InvoiceStatus = 'draft' | 'sent' | 'paid' | 'overdue';

export interface InvoiceLineItem {
  id: string;
  invoiceId: string;
  timesheetId: string | null;
  staffName: string;
  role: string;
  shiftDate: string;
  hours: number;
  rate: number;
  description: string;
  amount: number;
}

export interface Invoice {
  id: string;
  invoiceNumber: string;
  client: string;
  clientEmail: string;
  clientAddress: string;
  periodStart: string;
  periodEnd: string;
  issueDate: string;
  dueDate: string;
  notes: string;
  status: InvoiceStatus;
  taxRate: number;
  subtotal: number;
  taxAmount: number;
  total: number;
  lineItems: InvoiceLineItem[];
  createdAt: string;
}

function mapInvoice(r: Record<string, unknown>, lineItems: InvoiceLineItem[] = []): Invoice {
  return {
    id: r.id as string,
    invoiceNumber: r.invoice_number as string,
    client: r.client as string,
    clientEmail: r.client_email as string,
    clientAddress: r.client_address as string,
    periodStart: r.period_start as string,
    periodEnd: r.period_end as string,
    issueDate: r.issue_date as string,
    dueDate: r.due_date as string,
    notes: r.notes as string,
    status: r.status as InvoiceStatus,
    taxRate: parseFloat(r.tax_rate as string) || 0,
    subtotal: parseFloat(r.subtotal as string) || 0,
    taxAmount: parseFloat(r.tax_amount as string) || 0,
    total: parseFloat(r.total as string) || 0,
    lineItems,
    createdAt: r.created_at as string,
  };
}

function mapLineItem(r: Record<string, unknown>): InvoiceLineItem {
  return {
    id: r.id as string,
    invoiceId: r.invoice_id as string,
    timesheetId: r.timesheet_id as string | null,
    staffName: r.staff_name as string,
    role: r.role as string,
    shiftDate: r.shift_date as string,
    hours: parseFloat(r.hours as string) || 0,
    rate: parseFloat(r.rate as string) || 0,
    description: r.description as string,
    amount: parseFloat(r.amount as string) || 0,
  };
}

export const invoicesService = {
  async getAll(): Promise<Invoice[]> {
    const supabase = createClient();
    try {
      const { data: invoiceData, error: invError } = await supabase
        .from('invoices')
        .select('*')
        .order('created_at', { ascending: false });
      if (invError) {
        if (isSchemaError(invError)) throw invError;
        return [];
      }
      const { data: lineData, error: lineError } = await supabase
        .from('invoice_line_items')
        .select('*')
        .order('shift_date', { ascending: true });
      if (lineError) {
        if (isSchemaError(lineError)) throw lineError;
      }
      const linesByInvoice: Record<string, InvoiceLineItem[]> = {};
      for (const li of lineData || []) {
        const mapped = mapLineItem(li as Record<string, unknown>);
        if (!linesByInvoice[mapped.invoiceId]) linesByInvoice[mapped.invoiceId] = [];
        linesByInvoice[mapped.invoiceId].push(mapped);
      }
      return (invoiceData || []).map((r) => mapInvoice(r as Record<string, unknown>, linesByInvoice[r.id] || []));
    } catch (e: unknown) {
      const err = e as { message?: string };
      console.error('invoicesService.getAll error:', err?.message);
      throw e;
    }
  },

  async create(invoice: Omit<Invoice, 'id' | 'createdAt' | 'lineItems'>, lineItems: Omit<InvoiceLineItem, 'id' | 'invoiceId' | 'amount'>[]): Promise<Invoice | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('invoices')
        .insert({
          invoice_number: invoice.invoiceNumber,
          client: invoice.client,
          client_email: invoice.clientEmail,
          client_address: invoice.clientAddress,
          period_start: invoice.periodStart,
          period_end: invoice.periodEnd,
          issue_date: invoice.issueDate,
          due_date: invoice.dueDate,
          notes: invoice.notes,
          status: invoice.status,
          tax_rate: invoice.taxRate,
          subtotal: invoice.subtotal,
          tax_amount: invoice.taxAmount,
          total: invoice.total,
        })
        .select()
        .single();
      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }
      const invoiceId = data.id;
      if (lineItems.length > 0) {
        const { error: liError } = await supabase.from('invoice_line_items').insert(
          lineItems.map((li) => ({
            invoice_id: invoiceId,
            timesheet_id: li.timesheetId || null,
            staff_name: li.staffName,
            role: li.role,
            shift_date: li.shiftDate,
            hours: li.hours,
            rate: li.rate,
            description: li.description,
          }))
        );
        if (liError && isSchemaError(liError)) throw liError;
      }
      return mapInvoice(data as Record<string, unknown>, lineItems.map((li, i) => ({ ...li, id: `temp-${i}`, invoiceId, amount: li.hours * li.rate })));
    } catch (e: unknown) {
      const err = e as { message?: string };
      console.error('invoicesService.create error:', err?.message);
      throw e;
    }
  },

  async update(id: string, updates: Partial<Omit<Invoice, 'id' | 'createdAt' | 'lineItems'>>, lineItems?: Omit<InvoiceLineItem, 'id' | 'invoiceId' | 'amount'>[]): Promise<Invoice | null> {
    const supabase = createClient();
    try {
      const dbUpdates: Record<string, unknown> = {};
      if (updates.status !== undefined) dbUpdates.status = updates.status;
      if (updates.client !== undefined) dbUpdates.client = updates.client;
      if (updates.clientEmail !== undefined) dbUpdates.client_email = updates.clientEmail;
      if (updates.clientAddress !== undefined) dbUpdates.client_address = updates.clientAddress;
      if (updates.periodStart !== undefined) dbUpdates.period_start = updates.periodStart;
      if (updates.periodEnd !== undefined) dbUpdates.period_end = updates.periodEnd;
      if (updates.issueDate !== undefined) dbUpdates.issue_date = updates.issueDate;
      if (updates.dueDate !== undefined) dbUpdates.due_date = updates.dueDate;
      if (updates.notes !== undefined) dbUpdates.notes = updates.notes;
      if (updates.taxRate !== undefined) dbUpdates.tax_rate = updates.taxRate;
      if (updates.subtotal !== undefined) dbUpdates.subtotal = updates.subtotal;
      if (updates.taxAmount !== undefined) dbUpdates.tax_amount = updates.taxAmount;
      if (updates.total !== undefined) dbUpdates.total = updates.total;

      const { data, error } = await supabase
        .from('invoices')
        .update(dbUpdates)
        .eq('id', id)
        .select()
        .single();
      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }

      if (lineItems !== undefined) {
        await supabase.from('invoice_line_items').delete().eq('invoice_id', id);
        if (lineItems.length > 0) {
          await supabase.from('invoice_line_items').insert(
            lineItems.map((li) => ({
              invoice_id: id,
              timesheet_id: li.timesheetId || null,
              staff_name: li.staffName,
              role: li.role,
              shift_date: li.shiftDate,
              hours: li.hours,
              rate: li.rate,
              description: li.description,
            }))
          );
        }
      }

      const { data: freshLines } = await supabase.from('invoice_line_items').select('*').eq('invoice_id', id);
      return mapInvoice(data as Record<string, unknown>, (freshLines || []).map((li) => mapLineItem(li as Record<string, unknown>)));
    } catch (e: unknown) {
      const err = e as { message?: string };
      console.error('invoicesService.update error:', err?.message);
      throw e;
    }
  },

  async delete(id: string): Promise<boolean> {
    const supabase = createClient();
    try {
      const { error } = await supabase.from('invoices').delete().eq('id', id);
      if (error) {
        if (isSchemaError(error)) throw error;
        return false;
      }
      return true;
    } catch (e: unknown) {
      const err = e as { message?: string };
      console.error('invoicesService.delete error:', err?.message);
      return false;
    }
  },

  async getApprovedTimesheets(periodStart: string, periodEnd: string): Promise<Timesheet[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('timesheets')
        .select('*')
        .eq('approval_status', 'approved')
        .gte('shift_date', periodStart)
        .lte('shift_date', periodEnd)
        .order('shift_date', { ascending: true });
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return (data || []).map((r) => ({
        id: r.id,
        staffName: r.staff_name,
        role: r.role as StaffRole,
        specialty: r.specialty,
        facility: r.facility,
        shiftDate: r.shift_date,
        clockIn: r.clock_in,
        clockOut: r.clock_out,
        breakMin: r.break_min,
        regularHours: parseFloat(r.regular_hours),
        otHours: parseFloat(r.ot_hours),
        approvalStatus: r.approval_status as ApprovalStatus,
        missedPunch: r.missed_punch,
      }));
    } catch (e: unknown) {
      const err = e as { message?: string };
      console.error('invoicesService.getApprovedTimesheets error:', err?.message);
      return [];
    }
  },

  subscribe(onInsert: (inv: Invoice) => void, onUpdate: (inv: Invoice) => void, onDelete: (id: string) => void) {
    const supabase = createClient();
    const channel = supabase
      .channel('invoices_realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'invoices' }, (payload) => {
        if (payload.eventType === 'INSERT') onInsert(mapInvoice(payload.new as Record<string, unknown>));
        else if (payload.eventType === 'UPDATE') onUpdate(mapInvoice(payload.new as Record<string, unknown>));
        else if (payload.eventType === 'DELETE') onDelete((payload.old as Record<string, unknown>).id as string);
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  },
};

// ─── Audit Service ────────────────────────────────────────────────────────────

function mapAuditLog(r: Record<string, unknown>): AuditLog {
  return {
    id: r.id as string,
    performedBy: r.performed_by as string | null,
    performedByName: (r.performed_by_name as string) || '',
    performedByRole: (r.performed_by_role as string) || '',
    actionType: r.action_type as AuditActionType,
    severity: r.severity as AuditSeverity,
    targetEntity: (r.target_entity as string) || '',
    targetId: r.target_id as string | null,
    description: (r.description as string) || '',
    beforeState: r.before_state as Record<string, unknown> | null,
    afterState: r.after_state as Record<string, unknown> | null,
    ipAddress: r.ip_address as string | null,
    userAgent: r.user_agent as string | null,
    createdAt: r.created_at as string,
  };
}

export const auditService = {
  async getAll(filters: AuditFilters = {}): Promise<{ data: AuditLog[]; count: number }> {
    const supabase = createClient();
    try {
      let query = supabase
        .from('audit_logs')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false })
        .limit(200);

      if (filters.actionType) {
        query = query.eq('action_type', filters.actionType);
      }
      if (filters.severity) {
        query = query.eq('severity', filters.severity);
      }
      if (filters.dateFrom) {
        query = query.gte('created_at', filters.dateFrom);
      }
      if (filters.dateTo) {
        query = query.lte('created_at', filters.dateTo + 'T23:59:59');
      }
      if (filters.search) {
        const s = `%${filters.search}%`;
        query = query.or(`description.ilike.${s},performed_by_name.ilike.${s},target_entity.ilike.${s}`);
      }

      const { data, error, count } = await query;
      if (error) {
        if (isSchemaError(error)) throw error;
        return { data: [], count: 0 };
      }
      return {
        data: (data || []).map((r) => mapAuditLog(r as Record<string, unknown>)),
        count: count || 0,
      };
    } catch (e: unknown) {
      const err = e as { message?: string };
      console.error('auditService.getAll error:', err?.message);
      return { data: [], count: 0 };
    }
  },

  async log(entry: Omit<AuditLog, 'id' | 'createdAt'>): Promise<AuditLog | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('audit_logs')
        .insert({
          performed_by: entry.performedBy,
          performed_by_name: entry.performedByName,
          performed_by_role: entry.performedByRole,
          action_type: entry.actionType,
          severity: entry.severity,
          target_entity: entry.targetEntity,
          target_id: entry.targetId,
          description: entry.description,
          before_state: entry.beforeState,
          after_state: entry.afterState,
          ip_address: entry.ipAddress,
          user_agent: entry.userAgent,
        })
        .select()
        .single();
      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }
      return mapAuditLog(data as Record<string, unknown>);
    } catch (e: unknown) {
      const err = e as { message?: string };
      console.error('auditService.log error:', err?.message);
      return null;
    }
  },
};

// ─── Reports Service ──────────────────────────────────────────────────────────

export type ReportType = 'billable_hours' | 'staffing_utilization' | 'credential_compliance' | 'invoice_summary' | 'audit_export';
export type ReportStatus = 'pending' | 'completed' | 'failed';

export interface ReportExport {
  id: string;
  reportType: ReportType;
  title: string;
  generatedBy: string | null;
  generatedByName: string;
  periodStart: string | null;
  periodEnd: string | null;
  filters: Record<string, unknown>;
  rowCount: number;
  fileName: string | null;
  status: ReportStatus;
  notes: string;
  createdAt: string;
}

function mapReportExport(r: Record<string, unknown>): ReportExport {
  return {
    id: r.id as string,
    reportType: r.report_type as ReportType,
    title: r.title as string,
    generatedBy: r.generated_by as string | null,
    generatedByName: (r.generated_by_name as string) || '',
    periodStart: r.period_start as string | null,
    periodEnd: r.period_end as string | null,
    filters: (r.filters as Record<string, unknown>) || {},
    rowCount: (r.row_count as number) || 0,
    fileName: r.file_name as string | null,
    status: r.status as ReportStatus,
    notes: (r.notes as string) || '',
    createdAt: r.created_at as string,
  };
}

export const reportsService = {
  async getExportHistory(): Promise<ReportExport[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('reports_exports')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return (data || []).map((r) => mapReportExport(r as Record<string, unknown>));
    } catch (e: unknown) {
      const err = e as { message?: string };
      console.error('reportsService.getExportHistory error:', err?.message);
      return [];
    }
  },

  async logExport(entry: Omit<ReportExport, 'id' | 'createdAt'>): Promise<ReportExport | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('reports_exports')
        .insert({
          report_type: entry.reportType,
          title: entry.title,
          generated_by: entry.generatedBy,
          generated_by_name: entry.generatedByName,
          period_start: entry.periodStart,
          period_end: entry.periodEnd,
          filters: entry.filters,
          row_count: entry.rowCount,
          file_name: entry.fileName,
          status: entry.status,
          notes: entry.notes,
        })
        .select()
        .single();
      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }
      return mapReportExport(data as Record<string, unknown>);
    } catch (e: unknown) {
      const err = e as { message?: string };
      console.error('reportsService.logExport error:', err?.message);
      return null;
    }
  },

  async getBillableHoursData(periodStart: string, periodEnd: string) {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('invoice_line_items')
        .select('*, invoices!inner(period_start, period_end, client, status)')
        .gte('invoices.period_start', periodStart)
        .lte('invoices.period_end', periodEnd);
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return data || [];
    } catch (e: unknown) {
      const err = e as { message?: string };
      console.error('reportsService.getBillableHoursData error:', err?.message);
      return [];
    }
  },

  async getTimesheetSummary(periodStart: string, periodEnd: string) {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('timesheets')
        .select('*')
        .gte('shift_date', periodStart)
        .lte('shift_date', periodEnd)
        .order('shift_date', { ascending: true });
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return (data || []).map((r) => ({
        id: r.id as string,
        staffName: r.staff_name as string,
        role: r.role as string,
        specialty: r.specialty as string,
        facility: r.facility as string,
        shiftDate: r.shift_date as string,
        regularHours: parseFloat(r.regular_hours) || 0,
        otHours: parseFloat(r.ot_hours) || 0,
        approvalStatus: r.approval_status as string,
      }));
    } catch (e: unknown) {
      const err = e as { message?: string };
      console.error('reportsService.getTimesheetSummary error:', err?.message);
      return [];
    }
  },

  async getInvoiceSummary(periodStart: string, periodEnd: string) {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('invoices')
        .select('*')
        .gte('period_start', periodStart)
        .lte('period_end', periodEnd)
        .order('created_at', { ascending: false });
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return (data || []).map((r) => ({
        id: r.id as string,
        invoiceNumber: r.invoice_number as string,
        client: r.client as string,
        periodStart: r.period_start as string,
        periodEnd: r.period_end as string,
        status: r.status as string,
        subtotal: parseFloat(r.subtotal) || 0,
        taxAmount: parseFloat(r.tax_amount) || 0,
        total: parseFloat(r.total) || 0,
        issueDate: r.issue_date as string,
        dueDate: r.due_date as string,
      }));
    } catch (e: unknown) {
      const err = e as { message?: string };
      console.error('reportsService.getInvoiceSummary error:', err?.message);
      return [];
    }
  },

  async getCredentialComplianceSummary() {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('credentials')
        .select('*')
        .order('expiry_date', { ascending: true });
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return (data || []).map((r) => ({
        id: r.id as string,
        staffName: r.staff_name as string,
        role: r.role as string,
        department: r.department as string,
        credentialType: r.credential_type as string,
        credentialName: r.credential_name as string,
        issuingBody: r.issuing_body as string,
        licenseNumber: r.license_number as string,
        issueDate: r.issue_date as string,
        expiryDate: r.expiry_date as string,
        status: r.status as string,
        daysUntilExpiry: r.days_until_expiry as number,
        category: r.category as string,
      }));
    } catch (e: unknown) {
      const err = e as { message?: string };
      console.error('reportsService.getCredentialComplianceSummary error:', err?.message);
      return [];
    }
  },
};

// ─── Onboarding Types ─────────────────────────────────────────────────────────

export type OnboardingStatus = 'in_progress' | 'completed' | 'pending_review';
export type HireType = 'direct' | 'temp';
export type OnboardingCredentialStatus = 'pending' | 'uploaded' | 'verified' | 'rejected';

export interface NewHireOnboarding {
  id: string;
  fullName: string;
  email: string;
  phone: string;
  role: string;
  specialty: string;
  facility: string;
  department: string;
  hireType: HireType;
  agencyName: string;
  startDate: string | null;
  currentStep: number;
  onboardingStatus: OnboardingStatus;
  notes: string;
  createdAt: string;
  updatedAt: string;
}

export interface OnboardingCredential {
  id: string;
  onboardingId: string;
  credentialType: string;
  credentialName: string;
  issuingBody: string;
  licenseNumber: string;
  issueDate: string | null;
  expiryDate: string | null;
  fileName: string;
  fileUrl: string;
  uploadStatus: OnboardingCredentialStatus;
  notes: string;
  createdAt: string;
  updatedAt: string;
}

export interface OnboardingEmergencyContact {
  id: string;
  onboardingId: string;
  contactName: string;
  relationship: string;
  phonePrimary: string;
  phoneSecondary: string;
  email: string;
  address: string;
  isPrimary: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface OnboardingWithDetails extends NewHireOnboarding {
  credentials: OnboardingCredential[];
  emergencyContacts: OnboardingEmergencyContact[];
}

// ─── Onboarding Service ───────────────────────────────────────────────────────

function mapOnboarding(r: Record<string, unknown>): NewHireOnboarding {
  return {
    id: r.id as string,
    fullName: r.full_name as string,
    email: r.email as string,
    phone: r.phone as string,
    role: r.role as string,
    specialty: r.specialty as string,
    facility: r.facility as string,
    department: r.department as string,
    hireType: r.hire_type as HireType,
    agencyName: r.agency_name as string,
    startDate: r.start_date as string | null,
    currentStep: r.current_step as number,
    onboardingStatus: r.onboarding_status as OnboardingStatus,
    notes: r.notes as string,
    createdAt: r.created_at as string,
    updatedAt: r.updated_at as string,
  };
}

function mapCredential(r: Record<string, unknown>): OnboardingCredential {
  return {
    id: r.id as string,
    onboardingId: r.onboarding_id as string,
    credentialType: r.credential_type as string,
    credentialName: r.credential_name as string,
    issuingBody: r.issuing_body as string,
    licenseNumber: r.license_number as string,
    issueDate: r.issue_date as string | null,
    expiryDate: r.expiry_date as string | null,
    fileName: r.file_name as string,
    fileUrl: r.file_url as string,
    uploadStatus: r.upload_status as OnboardingCredentialStatus,
    notes: r.notes as string,
    createdAt: r.created_at as string,
    updatedAt: r.updated_at as string,
  };
}

function mapEmergencyContact(r: Record<string, unknown>): OnboardingEmergencyContact {
  return {
    id: r.id as string,
    onboardingId: r.onboarding_id as string,
    contactName: r.contact_name as string,
    relationship: r.relationship as string,
    phonePrimary: r.phone_primary as string,
    phoneSecondary: r.phone_secondary as string,
    email: r.email as string,
    address: r.address as string,
    isPrimary: r.is_primary as boolean,
    createdAt: r.created_at as string,
    updatedAt: r.updated_at as string,
  };
}

export const onboardingService = {
  async getAll(): Promise<NewHireOnboarding[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('new_hire_onboarding')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) { if (isSchemaError(error)) throw error; return []; }
      return (data || []).map((r) => mapOnboarding(r as Record<string, unknown>));
    } catch (e: unknown) {
      console.error('onboardingService.getAll error:', (e as { message?: string })?.message);
      throw e;
    }
  },

  async getById(id: string): Promise<OnboardingWithDetails | null> {
    const supabase = createClient();
    try {
      const results = await Promise.all([
          supabase.from('new_hire_onboarding').select('*').eq('id', id).single(),
          supabase.from('onboarding_credentials').select('*').eq('onboarding_id', id).order('created_at'),
          supabase.from('onboarding_emergency_contacts').select('*').eq('onboarding_id', id).order('is_primary', { ascending: false }),
        ]);
      const hire = results[0].data;
      const hireErr = results[0].error;
      const creds = results[1].data;
      const contacts = results[2].data;
      if (hireErr) { if (isSchemaError(hireErr)) throw hireErr; return null; }
      return {
        ...mapOnboarding(hire as Record<string, unknown>),
        credentials: (creds || []).map((r) => mapCredential(r as Record<string, unknown>)),
        emergencyContacts: (contacts || []).map((r) => mapEmergencyContact(r as Record<string, unknown>)),
      };
    } catch (e: unknown) {
      console.error('onboardingService.getById error:', (e as { message?: string })?.message);
      throw e;
    }
  },

  async create(payload: Omit<NewHireOnboarding, 'id' | 'createdAt' | 'updatedAt'>): Promise<NewHireOnboarding | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('new_hire_onboarding')
        .insert({
          full_name: payload.fullName,
          email: payload.email,
          phone: payload.phone,
          role: payload.role,
          specialty: payload.specialty,
          facility: payload.facility,
          department: payload.department,
          hire_type: payload.hireType,
          agency_name: payload.agencyName,
          start_date: payload.startDate,
          current_step: payload.currentStep,
          onboarding_status: payload.onboardingStatus,
          notes: payload.notes,
        })
        .select()
        .single();
      if (error) { if (isSchemaError(error)) throw error; return null; }
      return mapOnboarding(data as Record<string, unknown>);
    } catch (e: unknown) {
      console.error('onboardingService.create error:', (e as { message?: string })?.message);
      throw e;
    }
  },

  async update(id: string, payload: Partial<Omit<NewHireOnboarding, 'id' | 'createdAt' | 'updatedAt'>>): Promise<NewHireOnboarding | null> {
    const supabase = createClient();
    const dbUpdates: Record<string, unknown> = {};
    if ('fullName' in payload) dbUpdates.full_name = payload.fullName;
    if ('email' in payload) dbUpdates.email = payload.email;
    if ('phone' in payload) dbUpdates.phone = payload.phone;
    if ('role' in payload) dbUpdates.role = payload.role;
    if ('specialty' in payload) dbUpdates.specialty = payload.specialty;
    if ('facility' in payload) dbUpdates.facility = payload.facility;
    if ('department' in payload) dbUpdates.department = payload.department;
    if ('hireType' in payload) dbUpdates.hire_type = payload.hireType;
    if ('agencyName' in payload) dbUpdates.agency_name = payload.agencyName;
    if ('startDate' in payload) dbUpdates.start_date = payload.startDate;
    if ('currentStep' in payload) dbUpdates.current_step = payload.currentStep;
    if ('onboardingStatus' in payload) dbUpdates.onboarding_status = payload.onboardingStatus;
    if ('notes' in payload) dbUpdates.notes = payload.notes;
    try {
      const { data, error } = await supabase
        .from('new_hire_onboarding')
        .update(dbUpdates)
        .eq('id', id)
        .select()
        .single();
      if (error) { if (isSchemaError(error)) throw error; return null; }
      return mapOnboarding(data as Record<string, unknown>);
    } catch (e: unknown) {
      console.error('onboardingService.update error:', (e as { message?: string })?.message);
      throw e;
    }
  },

  async delete(id: string): Promise<boolean> {
    const supabase = createClient();
    try {
      const { error } = await supabase.from('new_hire_onboarding').delete().eq('id', id);
      if (error) { if (isSchemaError(error)) throw error; return false; }
      return true;
    } catch (e: unknown) {
      console.error('onboardingService.delete error:', (e as { message?: string })?.message);
      throw e;
    }
  },

  // Credentials
  async addCredential(payload: Omit<OnboardingCredential, 'id' | 'createdAt' | 'updatedAt'>): Promise<OnboardingCredential | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('onboarding_credentials')
        .insert({
          onboarding_id: payload.onboardingId,
          credential_type: payload.credentialType,
          credential_name: payload.credentialName,
          issuing_body: payload.issuingBody,
          license_number: payload.licenseNumber,
          issue_date: payload.issueDate,
          expiry_date: payload.expiryDate,
          file_name: payload.fileName,
          file_url: payload.fileUrl,
          upload_status: payload.uploadStatus,
          notes: payload.notes,
        })
        .select()
        .single();
      if (error) { if (isSchemaError(error)) throw error; return null; }
      return mapCredential(data as Record<string, unknown>);
    } catch (e: unknown) {
      console.error('onboardingService.addCredential error:', (e as { message?: string })?.message);
      throw e;
    }
  },

  async updateCredential(id: string, payload: Partial<Omit<OnboardingCredential, 'id' | 'onboardingId' | 'createdAt' | 'updatedAt'>>): Promise<OnboardingCredential | null> {
    const supabase = createClient();
    const dbUpdates: Record<string, unknown> = {};
    if ('credentialType' in payload) dbUpdates.credential_type = payload.credentialType;
    if ('credentialName' in payload) dbUpdates.credential_name = payload.credentialName;
    if ('issuingBody' in payload) dbUpdates.issuing_body = payload.issuingBody;
    if ('licenseNumber' in payload) dbUpdates.license_number = payload.licenseNumber;
    if ('issueDate' in payload) dbUpdates.issue_date = payload.issueDate;
    if ('expiryDate' in payload) dbUpdates.expiry_date = payload.expiryDate;
    if ('fileName' in payload) dbUpdates.file_name = payload.fileName;
    if ('fileUrl' in payload) dbUpdates.file_url = payload.fileUrl;
    if ('uploadStatus' in payload) dbUpdates.upload_status = payload.uploadStatus;
    if ('notes' in payload) dbUpdates.notes = payload.notes;
    try {
      const { data, error } = await supabase
        .from('onboarding_credentials')
        .update(dbUpdates)
        .eq('id', id)
        .select()
        .single();
      if (error) { if (isSchemaError(error)) throw error; return null; }
      return mapCredential(data as Record<string, unknown>);
    } catch (e: unknown) {
      console.error('onboardingService.updateCredential error:', (e as { message?: string })?.message);
      throw e;
    }
  },

  async deleteCredential(id: string): Promise<boolean> {
    const supabase = createClient();
    try {
      const { error } = await supabase.from('onboarding_credentials').delete().eq('id', id);
      if (error) { if (isSchemaError(error)) throw error; return false; }
      return true;
    } catch (e: unknown) {
      console.error('onboardingService.deleteCredential error:', (e as { message?: string })?.message);
      throw e;
    }
  },

  // Emergency Contacts
  async addEmergencyContact(payload: Omit<OnboardingEmergencyContact, 'id' | 'createdAt' | 'updatedAt'>): Promise<OnboardingEmergencyContact | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('onboarding_emergency_contacts')
        .insert({
          onboarding_id: payload.onboardingId,
          contact_name: payload.contactName,
          relationship: payload.relationship,
          phone_primary: payload.phonePrimary,
          phone_secondary: payload.phoneSecondary,
          email: payload.email,
          address: payload.address,
          is_primary: payload.isPrimary,
        })
        .select()
        .single();
      if (error) { if (isSchemaError(error)) throw error; return null; }
      return mapEmergencyContact(data as Record<string, unknown>);
    } catch (e: unknown) {
      console.error('onboardingService.addEmergencyContact error:', (e as { message?: string })?.message);
      throw e;
    }
  },

  async updateEmergencyContact(id: string, payload: Partial<Omit<OnboardingEmergencyContact, 'id' | 'onboardingId' | 'createdAt' | 'updatedAt'>>): Promise<OnboardingEmergencyContact | null> {
    const supabase = createClient();
    const dbUpdates: Record<string, unknown> = {};
    if ('contactName' in payload) dbUpdates.contact_name = payload.contactName;
    if ('relationship' in payload) dbUpdates.relationship = payload.relationship;
    if ('phonePrimary' in payload) dbUpdates.phone_primary = payload.phonePrimary;
    if ('phoneSecondary' in payload) dbUpdates.phone_secondary = payload.phoneSecondary;
    if ('email' in payload) dbUpdates.email = payload.email;
    if ('address' in payload) dbUpdates.address = payload.address;
    if ('isPrimary' in payload) dbUpdates.is_primary = payload.isPrimary;
    try {
      const { data, error } = await supabase
        .from('onboarding_emergency_contacts')
        .update(dbUpdates)
        .eq('id', id)
        .select()
        .single();
      if (error) { if (isSchemaError(error)) throw error; return null; }
      return mapEmergencyContact(data as Record<string, unknown>);
    } catch (e: unknown) {
      console.error('onboardingService.updateEmergencyContact error:', (e as { message?: string })?.message);
      throw e;
    }
  },

  async deleteEmergencyContact(id: string): Promise<boolean> {
    const supabase = createClient();
    try {
      const { error } = await supabase.from('onboarding_emergency_contacts').delete().eq('id', id);
      if (error) { if (isSchemaError(error)) throw error; return false; }
      return true;
    } catch (e: unknown) {
      console.error('onboardingService.deleteEmergencyContact error:', (e as { message?: string })?.message);
      throw e;
    }
  },
};
